import { useState, FormEvent, useEffect, useRef } from 'react';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Alert,
  Link,
  CircularProgress,
  InputAdornment,
  Stack,
} from '@mui/material';
import { HealthAndSafety, Badge, Email, Login as LoginIcon } from '@mui/icons-material';
import { supabase } from '../../services/supabase';
import { requestPasswordRecovery } from '../../services/auth';
import { cuitDigits, cuitToLoginEmail } from '../../utils/format';

const OTP_LENGTH = 6;

/**
 * Acceso con codigo OTP de 6 digitos.
 *
 * Flujo simplificado:
 *  1. Usuario ingresa email + codigo OTP (de email)
 *  2. verifyOtp({ type: 'recovery' }) crea una sesion activa
 *  3. Quedamos logueados y redirigimos al dashboard
 *
 * No se pide nueva contrasena: si el usuario quiere cambiar la clave,
 * lo hace despues desde el menu "Cambiar contrasena".
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [cuit, setCuit] = useState(searchParams.get('cuit') ?? '');
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Auto-focus en el primer input de OTP cuando hay email pre-cargado
  useEffect(() => {
    if (email) {
      otpRefs.current[0]?.focus();
    }
  }, []);

  // ---------- OTP handlers ----------
  const handleOtpChange = (idx: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    if (!digit && value !== '') return;
    const newOtp = [...otp];
    newOtp[idx] = digit;
    setOtp(newOtp);
    if (digit && idx < OTP_LENGTH - 1) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx: number, e: KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) otpRefs.current[idx - 1]?.focus();
    if (e.key === 'ArrowLeft' && idx > 0) otpRefs.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpPaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const newOtp = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) newOtp[i] = pasted[i];
    setOtp(newOtp);
    const nextEmpty = newOtp.findIndex((v) => !v);
    const targetIdx = nextEmpty === -1 ? OTP_LENGTH - 1 : nextEmpty;
    otpRefs.current[targetIdx]?.focus();
  };

  const otpValue = otp.join('');
  const otpComplete = otpValue.length === OTP_LENGTH;

  // ---------- Reenviar codigo ----------
  const handleResend = async () => {
    if (!cuit || !email) {
      setError('Ingresa tu CUIT y tu correo para reenviar el codigo.');
      return;
    }
    setError(null);
    setResendMessage(null);
    setResending(true);
    const cleanEmail = email.trim().toLowerCase();
    try {
      await requestPasswordRecovery(cuit, cleanEmail);
    } catch (_err) {
      setResending(false);
      setError('No pudimos reenviar el codigo. Intenta nuevamente.');
      return;
    }
    setResending(false);
    setResendMessage('Se envio un nuevo codigo. Revisa tu correo.');
    setOtp(Array(OTP_LENGTH).fill(''));
    otpRefs.current[0]?.focus();
  };

  // ---------- Submit ----------
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResendMessage(null);

    const digits = cuitDigits(cuit);
    if (digits.length !== 11) {
      setError('Ingresa tu CUIT.');
      return;
    }
    if (!otpComplete) {
      setError(`Ingresa el codigo de ${OTP_LENGTH} digitos.`);
      return;
    }

    setSubmitting(true);

    // El OTP se validó contra la identidad de auth (email sintético del CUIT).
    const { error: otpError } = await supabase.auth.verifyOtp({
      email: cuitToLoginEmail(digits),
      token: otpValue,
      type: 'recovery',
    });

    if (otpError) {
      setSubmitting(false);
      const msg = otpError.message.toLowerCase();
      if (msg.includes('expired') || msg.includes('invalid')) {
        setError('El codigo es invalido o expiro. Solicita uno nuevo.');
      } else {
        setError('No pudimos validar el codigo. Intenta nuevamente.');
      }
      setOtp(Array(OTP_LENGTH).fill(''));
      otpRefs.current[0]?.focus();
      return;
    }

    // verifyOtp dejo al usuario logueado. El AuthContext detecta la sesion
    // y redirigimos al dashboard.
    navigate('/', { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0d47a1 0%, #1565C0 50%, #42a5f5 100%)',
        p: 2,
      }}
    >
      <Card sx={{ width: 460, maxWidth: '100%', boxShadow: 8 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
              }}
            >
              <HealthAndSafety sx={{ fontSize: 36 }} />
            </Avatar>
            <Typography variant="h5" fontWeight={700} color="primary">
              Ingresar con codigo
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Ingresa el codigo de 6 digitos que enviamos a tu correo para
              acceder al sistema.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit} noValidate>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {resendMessage && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResendMessage(null)}>
                {resendMessage}
              </Alert>
            )}

            <TextField
              fullWidth
              required
              label="CUIT"
              type="text"
              inputMode="numeric"
              placeholder="20-12345678-3"
              value={cuit}
              onChange={(e) => setCuit(e.target.value)}
              disabled={submitting}
              autoComplete="username"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Badge color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              required
              label="Correo electronico"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              autoComplete="email"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* OTP boxes */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Codigo de verificacion
            </Typography>
            <Stack
              direction="row"
              spacing={{ xs: 0.5, sm: 1 }}
              sx={{ mb: 3, justifyContent: 'space-between' }}
            >
              {otp.map((digit, idx) => (
                <TextField
                  key={idx}
                  inputRef={(el) => (otpRefs.current[idx] = el)}
                  value={digit}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                  onPaste={idx === 0 ? handleOtpPaste : undefined}
                  disabled={submitting}
                  inputProps={{
                    maxLength: 1,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    'aria-label': `Digito ${idx + 1}`,
                    style: {
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      padding: '12px 0',
                    },
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderWidth: digit ? 2 : 1 },
                    },
                  }}
                />
              ))}
            </Stack>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || !cuit || !email || !otpComplete}
              startIcon={
                submitting ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />
              }
              sx={{ py: 1.5, mb: 2 }}
            >
              {submitting ? 'Verificando...' : 'Ingresar'}
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                size="small"
                onClick={handleResend}
                disabled={resending || submitting}
              >
                {resending ? 'Reenviando...' : 'Reenviar codigo'}
              </Button>
              <Link
                component={RouterLink}
                to="/auth/login"
                variant="body2"
                underline="hover"
              >
                Volver al login
              </Link>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
